/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SendTab - Control de Envío Masivo WhatsApp
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file SendTab.tsx
 * @version 1.2.0
 * @description Tab profesional con integración Sentinela para envío async
 * @created 2025-11-09
 * @updated 2025-11-09 - Integración Sentinela + CSV universal + tracking
 * 
 * FEATURES:
 * - ✅ Checklist visual de 4 requisitos obligatorios
 * - 🎯 Preview en vivo del mensaje con contactos muestra
 * - 📊 Layout 70/30 (control + preview)
 * - 🚀 Integración con Sentinela (envío asíncrono)
 * - 📝 Generación CSV universal estandarizado
 * - 📈 Tracking de estado de envío
 * - 🔒 Validaciones estrictas pre-envío
 * - 🎨 Diseño universal (no comercial específico)
 * - 🌐 Soporte ES/EN completo
 */

/**
 * SendTab.tsx v1.3.0 - Con Polling Real-Time + Lista Jobs
 * 
 * CAMBIOS v1.3.0:
 * - ✅ Polling HTTP cada 5s para actualizar progreso
 * - ✅ Lista de jobs encolados/en proceso
 * - ✅ Permite múltiples envíos programados
 * - ✅ Detección automática de jobs activos del usuario
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Badge, ProgressBar, Form, ListGroup, Alert, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faClock, faCheckCircle, faExclamationTriangle, faUsers, faFileAlt,
  faCalendarAlt, faCog, faTimesCircle, faEye, faChartLine, faBolt, faCheckDouble,
  faSpinner, faCheck, faSync, faTrash, faPause, faPlay
} from '@fortawesome/free-solid-svg-icons';
import type { WhatsAppRiskAssessment } from '../../types/bulksend.types';
import axios from 'axios';
import { getHatateToken } from '../../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SendResult {
  success: boolean;
  totalSent: number;
  totalFailed: number;
  startTime: string;
  endTime?: string;
}

export interface ConfigValidation {
  isValid: boolean;
  hasSchedule: boolean;
  hasEnvConfig: boolean;
}

export interface SendJob {
  jobId: string;
  userId: string;
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'error';
  totalContacts: number;
  sentContacts: number;
  failedContacts?: number;
  filePath: string;
  createdAt: string;
  updatedAt?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  progress: number;
}

export interface SendTabProps {
  language?: 'es' | 'en';
  contacts: any[];
  message: string;
  riskAssessment?: WhatsAppRiskAssessment;
  configValidation?: ConfigValidation;
  userId: string; // ⭐ NUEVO: Requerido para filtrar jobs
  onSendStart?: () => void;
  onSendComplete?: (result: SendResult) => void;
}

interface SendProgress {
  isActive: boolean;
  current: number;
  total: number;
  percentage: number;
  startTime?: Date;
  status?: 'pending' | 'queued' | 'processing' | 'completed' | 'error';
  message?: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  isValid: boolean;
  icon: any;
  value?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const SendTab: React.FC<SendTabProps> = ({
  language = 'es',
  contacts = [],
  message = '',
  riskAssessment,
  configValidation = { isValid: true, hasSchedule: true, hasEnvConfig: true },
  userId,
  onSendStart,
  onSendComplete
}) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [sendMode, setSendMode] = useState<'immediate' | 'scheduled'>('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  
  // ⭐ NUEVO: Estados para polling y jobs
  const [jobs, setJobs] = useState<SendJob[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPollTime, setLastPollTime] = useState<Date | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [localProgress, setLocalProgress] = useState<SendProgress>({
    isActive: false,
    current: 0,
    total: 0,
    percentage: 0,
    status: 'pending'
  });
  
  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED VALUES
  // ───────────────────────────────────────────────────────────────────────
  
  const totalContacts = contacts.length;
  const messageLength = message.trim().length;
  const estimatedTime = totalContacts > 0 ? (totalContacts * 5) / 60 : 0;
  const estimatedTimeFormatted = estimatedTime > 1 
    ? `${estimatedTime.toFixed(1)}h` 
    : `${Math.round(estimatedTime * 60)}min`;
  
  const messagesPerHour = totalContacts > 0 ? Math.round(totalContacts / estimatedTime) : 0;
  const riskLevel = riskAssessment?.riskLevel || 'medium';
  const riskScore = riskAssessment?.overallScore || 0;
  
  const activeJob = useMemo(() => {
  // Primero buscar jobs activos (processing/queued)
  const active = jobs.find(j => j.status === 'processing' || j.status === 'queued');
  if (active) return active;
  
  // Si no hay activos, buscar el último job completado reciente (< 3 segundos)
  const recentCompleted = jobs.find(j => {
    if (j.status !== 'completed') return false;
    const completedTime = new Date(j.completedAt || j.updatedAt);
    const now = new Date();
    const diffSeconds = (now.getTime() - completedTime.getTime()) / 1000;
    return diffSeconds < 3;
  });
  
  return recentCompleted || null;
}, [jobs]);

  // ⭐ NUEVO: Progreso calculado (híbrido: local + polling)
  const progress = useMemo((): SendProgress => {
    // Si hay job del servidor, usarlo (prioridad)
    if (activeJob) {
      return {
        isActive: true,
        current: activeJob.sentContacts,
        total: activeJob.totalContacts,
        percentage: activeJob.progress,
        startTime: new Date(activeJob.startedAt || activeJob.createdAt),
        status: activeJob.status,
        message: activeJob.status === 'queued' 
          ? (language === 'es' ? 'En cola. Sentinela procesará según horarios.' : 'Queued. Sentinela will process by schedule.')
          : activeJob.status === 'processing'
          ? (language === 'es' ? `Enviando... ${activeJob.sentContacts}/${activeJob.totalContacts}` : `Sending... ${activeJob.sentContacts}/${activeJob.totalContacts}`)
          : (language === 'es' ? 'Procesando...' : 'Processing...')
      };
    }
    
    // Si no hay job del servidor pero hay progreso local, usarlo
    if (localProgress.isActive) {
      return localProgress;
    }
    
    return {
      isActive: false,
      current: 0,
      total: 0,
      percentage: 0,
      status: 'pending'
    };
  }, [activeJob, localProgress, language]);
  
  // Checklist de validación
  const checklist = useMemo(() => {
    const items: ChecklistItem[] = [
      {
        id: 'message',
        label: language === 'es' ? 'Mensaje preparado' : 'Message ready',
        isValid: messageLength >= 10,
        icon: faFileAlt,
        value: messageLength > 0 ? `${messageLength} ${language === 'es' ? 'caracteres' : 'characters'}` : undefined
      },
      {
        id: 'contacts',
        label: language === 'es' ? 'Destinatarios listos' : 'Recipients ready',
        isValid: totalContacts > 0,
        icon: faUsers,
        value: totalContacts > 0 ? `${totalContacts} ${language === 'es' ? 'contactos' : 'contacts'}` : undefined
      },
      {
        id: 'schedule',
        label: language === 'es' ? 'Horario configurado' : 'Schedule configured',
        isValid: configValidation.hasSchedule,
        icon: faCalendarAlt,
        value: configValidation.hasSchedule ? (language === 'es' ? 'Configurado' : 'Configured') : undefined
      },
      {
        id: 'config',
        label: language === 'es' ? 'Sistema validado' : 'System validated',
        isValid: configValidation.isValid && configValidation.hasEnvConfig,
        icon: faCog,
        value: configValidation.isValid ? (language === 'es' ? 'Todo OK' : 'All OK') : undefined
      }
    ];
    return items;
  }, [messageLength, totalContacts, configValidation, language]);
  
  const allValid = checklist.every(item => item.isValid);
  const validCount = checklist.filter(item => item.isValid).length;
  
  // ───────────────────────────────────────────────────────────────────────
  // ⭐ NUEVO: POLLING LOGIC
  // ───────────────────────────────────────────────────────────────────────
  
  /**
   * Fetch jobs del usuario desde Sentinela API
   */
  const fetchJobs = useCallback(async () => {
    try {
      const token = getHatateToken();
      if (!token || !userId) return;
      
      const response = await axios.get(
        `https://backend.anubisai.net/api/sentinela/jobs?user_id=${userId}&limit=20`,
        {
          headers: { 'Authorization': token },
          timeout: 5000
        }
      );
      
      if (response.data && response.data.jobs) {
        setJobs(response.data.jobs);
        setLastPollTime(new Date());
        
        // ⭐ CAMBIO: Si detecta job completado RECIENTE, mostrar completo y desactivar local
        const recentCompleted = response.data.jobs.find(
          (j: SendJob) => {
            if (j.status !== 'completed') return false;
            const completedTime = new Date(j.completedAt || j.updatedAt);
            const now = new Date();
            const diffSeconds = (now.getTime() - completedTime.getTime()) / 1000;
            return diffSeconds < 10; // Completado hace menos de 10s
          }
        );
        
        if (recentCompleted && localProgress.isActive) {
          // Mostrar progreso completo
          setLocalProgress({
            isActive: true,
            current: recentCompleted.totalContacts,
            total: recentCompleted.totalContacts,
            percentage: 100,
            status: 'completed',
            message: language === 'es' ? '¡Envío completado!' : 'Send completed!'
          });
          
          // Desactivar después de 2s
          setTimeout(() => {
            setLocalProgress({ isActive: false, current: 0, total: 0, percentage: 0, status: 'pending' });
          }, 2000);
          
          return;
        }
        
        // ⭐ Si detecta job activo del servidor, desactivar progreso local
        const serverActiveJob = response.data.jobs.find(
          (j: SendJob) => j.status === 'processing' || j.status === 'queued'
        );
        if (serverActiveJob && localProgress.isActive) {
          setLocalProgress({ isActive: false, current: 0, total: 0, percentage: 0, status: 'pending' });
        }
      }
    } catch (error: any) {
      console.error('[SendTab] Error fetching jobs:', error);
    }
  }, [userId, localProgress.isActive, language]);
  
  /**
   * Iniciar polling
   */
  const startPolling = useCallback(() => {
    if (isPolling) return;
    
    console.log('[SendTab] Starting polling...');
    setIsPolling(true);
    
    // Fetch inmediato
    fetchJobs();
  }, [isPolling, fetchJobs]);
  
  /**
   * Detener polling
   */
  const stopPolling = useCallback(() => {
    console.log('[SendTab] Stopping polling...');
    setIsPolling(false);
  }, []);
  
  /**
   * Effect: Polling interval
   */
  useEffect(() => {
    if (!isPolling) return;
    
    const interval = setInterval(() => {
      fetchJobs();
    }, 5000); // Cada 5 segundos
    
    return () => clearInterval(interval);
  }, [isPolling, fetchJobs]);
  
  /**
   * Effect: Auto-start polling si hay job activo o progreso local
   */
  useEffect(() => {
    if ((activeJob || localProgress.isActive) && !isPolling) {
      startPolling();
    }
    
    // Auto-stop si no hay jobs activos ni progreso local
    if (!activeJob && !localProgress.isActive && isPolling && jobs.length > 0) {
      const hasActiveJobs = jobs.some(j => j.status === 'processing' || j.status === 'queued');
      if (!hasActiveJobs) {
        stopPolling();
      }
    }
  }, [activeJob, localProgress.isActive, isPolling, jobs, startPolling, stopPolling]);
  
  /**
   * Effect: Fetch inicial al montar
   */
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleSendStart = async () => {
    if (!allValid) {
      const missingItems = checklist
        .filter(item => !item.isValid)
        .map(item => item.label)
        .join(', ');
      
      alert(language === 'es' 
        ? `Por favor, completa los siguientes requisitos: ${missingItems}` 
        : `Please complete the following requirements: ${missingItems}`
      );
      return;
    }
    
    if (sendMode === 'scheduled' && (!scheduledDate || !scheduledTime)) {
      alert(language === 'es' 
        ? 'Por favor, selecciona fecha y hora para el envío programado' 
        : 'Please select date and time for scheduled send'
      );
      return;
    }
    
    setIsSending(true);
    setSendError(null);
    
    try {
      const token = getHatateToken();
      if (!token) {
        throw new Error(language === 'es' 
          ? 'Token de autenticación no disponible' 
          : 'Authentication token not available');
      }
      
      if (onSendStart) {
        onSendStart();
      }
      
      // Generar CSV
      const csvContent = generateUniversalCSV();
      
      // Crear nombre de archivo con timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `contactos_${timestamp}.csv`;
      
      // Enviar a backend
      const response = await axios.post(
        'https://backend.anubisai.net/api/files/save-to-tmp',
        {
          filename: filename,
          content: csvContent,
          contentType: 'text/csv'
        },
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );
      
      if (response.status === 201 && response.data.success) {
        console.log('✅ CSV guardado exitosamente:', response.data.path);
        
        // ⭐ Activar progreso local INMEDIATAMENTE
        setLocalProgress({
          isActive: true,
          current: 0,
          total: totalContacts,
          percentage: 10,
          startTime: new Date(),
          status: 'queued',
          message: language === 'es' 
            ? 'Archivo enviado a Sentinela. Procesamiento iniciará según horarios configurados.' 
            : 'File sent to Sentinela. Processing will start according to configured schedule.'
        });
        
        // Iniciar polling inmediatamente
        startPolling();
        
        // Fetch inmediato para actualizar progreso
        await fetchJobs();
        
        // Actualizar progreso gradualmente mientras espera
        setTimeout(() => {
          setLocalProgress(prev => prev.isActive ? { ...prev, percentage: 25 } : prev);
          fetchJobs();
        }, 2000);
        
        setTimeout(() => {
          setLocalProgress(prev => prev.isActive ? { ...prev, percentage: 35 } : prev);
        }, 4000);
      } else {
        throw new Error(response.data.message || 'Error al guardar archivo');
      }
      
    } catch (error: any) {
      console.error('❌ Error al enviar:', error);
      
      if (error.response) {
        setSendError(
          `Error del servidor (${error.response.status}): ${error.response.data?.message || error.response.statusText}`
        );
      } else if (error.request) {
        setSendError(
          language === 'es' 
            ? 'Error de conexión con el servidor. Verifique su conexión a internet.' 
            : 'Connection error with server. Check your internet connection.'
        );
      } else {
        setSendError(error.message || 'Error desconocido');
      }
    } finally {
      setIsSending(false);
    }
  };
  
  /**
   * Generar CSV universal estandarizado para Sentinela
   * Formato: nombre,telefono,referencia,mensaje
   */
  const generateUniversalCSV = (): string => {
    const rows: string[] = [];
    
    contacts.forEach((contact) => {
      const nombre = contact.nombre || contact.name || 'Usuario';
      const telefono = contact.telefono || contact.phone || '';
      const referencia = contact.curso || contact.referencia || contact.reference || 'Información';
      
      // Personalizar mensaje (reemplazar variables)
      let mensajePersonalizado = message;
      mensajePersonalizado = mensajePersonalizado.replace(/\[nombre\]/gi, nombre);
      mensajePersonalizado = mensajePersonalizado.replace(/\[curso\]/gi, referencia);
      mensajePersonalizado = mensajePersonalizado.replace(/\[referencia\]/gi, referencia);
      
      // Escapar comillas y saltos de línea para CSV
      const escapedNombre = `"${nombre.replace(/"/g, '""')}"`;
      const escapedTelefono = telefono;
      const escapedReferencia = `"${referencia.replace(/"/g, '""')}"`;
      const escapedMensaje = `"${mensajePersonalizado.replace(/"/g, '""')}"`;
      
      rows.push(`${escapedNombre},${escapedTelefono},${escapedReferencia},${escapedMensaje}`);
    });
    
    return rows.join('\n');
  };
  
  /**
   * Refrescar manualmente
   */
  const handleRefresh = () => {
    fetchJobs();
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: CHECKLIST
  // ───────────────────────────────────────────────────────────────────────
  
  const renderChecklist = () => (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)', 
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px 20px' 
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ fontWeight: 600 }}>
            <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
            {language === 'es' ? 'Validación Pre-Envío' : 'Pre-Send Validation'}
          </h6>
          <Badge 
            bg={allValid ? 'success' : 'warning'}
            style={{ fontSize: '0.85rem', padding: '6px 12px' }}
          >
            {validCount}/4 {language === 'es' ? 'completado' : 'complete'}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body style={{ padding: '16px 20px' }}>
        <ListGroup variant="flush">
          {checklist.map((item) => (
            <ListGroup.Item
              key={item.id}
              style={{
                border: 'none',
                padding: '12px 0',
                borderBottom: '1px solid var(--bs-border-color)',
                backgroundColor: 'transparent'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: item.isValid ? '#10b98115' : '#f59e0b15',
                      border: `2px solid ${item.isValid ? '#10b981' : '#f59e0b'}`
                    }}
                  >
                    <FontAwesomeIcon
                      icon={item.isValid ? faCheckCircle : item.icon}
                      style={{ color: item.isValid ? '#10b981' : '#f59e0b', fontSize: '1rem' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: 'var(--bs-body-color)' }}>
                      {item.label}
                    </div>
                    {item.value && (
                      <small style={{ color: 'var(--bs-secondary)' }}>
                        {item.value}
                      </small>
                    )}
                  </div>
                </div>
                {item.isValid ? (
                  <Badge bg="success" style={{ fontSize: '0.75rem' }}>
                    {language === 'es' ? 'Listo' : 'Ready'}
                  </Badge>
                ) : (
                  <Badge bg="warning" style={{ fontSize: '0.75rem' }}>
                    {language === 'es' ? 'Pendiente' : 'Pending'}
                  </Badge>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Card.Body>
    </Card>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: ESTIMACIONES
  // ───────────────────────────────────────────────────────────────────────
  
  const renderEstimations = () => (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)', 
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px 20px' 
      }}>
        <h6 className="mb-0" style={{ fontWeight: 600 }}>
          <FontAwesomeIcon icon={faChartLine} className="me-2" />
          {language === 'es' ? 'Estimaciones' : 'Estimations'}
        </h6>
      </Card.Header>
      <Card.Body style={{ padding: '20px' }}>
        <Row>
          <Col md={4} className="text-center">
            <div style={{ 
              padding: '16px',
              backgroundColor: 'var(--bs-body-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: '8px'
            }}>
              <FontAwesomeIcon icon={faClock} style={{ fontSize: '1.5rem', color: '#3b82f6', marginBottom: '8px' }} />
              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--bs-body-color)' }}>
                {estimatedTimeFormatted}
              </div>
              <small style={{ color: 'var(--bs-secondary)' }}>
                {language === 'es' ? 'Duración estimada' : 'Estimated duration'}
              </small>
            </div>
          </Col>
          <Col md={4} className="text-center">
            <div style={{ 
              padding: '16px',
              backgroundColor: 'var(--bs-body-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: '8px'
            }}>
              <FontAwesomeIcon icon={faBolt} style={{ fontSize: '1.5rem', color: '#f59e0b', marginBottom: '8px' }} />
              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--bs-body-color)' }}>
                {messagesPerHour}
              </div>
              <small style={{ color: 'var(--bs-secondary)' }}>
                {language === 'es' ? 'Mensajes/hora' : 'Messages/hour'}
              </small>
            </div>
          </Col>
          <Col md={4} className="text-center">
            <div style={{ 
              padding: '16px',
              backgroundColor: riskLevel === 'low' ? '#10b98115' : riskLevel === 'medium' ? '#f59e0b15' : '#ef444415',
              border: `1px solid ${riskLevel === 'low' ? '#10b981' : riskLevel === 'medium' ? '#f59e0b' : '#ef4444'}`,
              borderRadius: '8px'
            }}>
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                style={{ 
                  fontSize: '1.5rem', 
                  color: riskLevel === 'low' ? '#10b981' : riskLevel === 'medium' ? '#f59e0b' : '#ef4444',
                  marginBottom: '8px' 
                }} 
              />
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 600, 
                color: riskLevel === 'low' ? '#10b981' : riskLevel === 'medium' ? '#f59e0b' : '#ef4444'
              }}>
                {riskScore}
              </div>
              <small style={{ color: 'var(--bs-secondary)' }}>
                {language === 'es' ? 'Nivel de riesgo' : 'Risk level'}
              </small>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: SEND MODES
  // ───────────────────────────────────────────────────────────────────────
  
  const renderSendModes = () => (
    <div className="mb-4">
      <h6 style={{ fontWeight: 600, marginBottom: '16px' }}>
        {language === 'es' ? 'Modo de Envío' : 'Send Mode'}
      </h6>
      <div className="d-flex gap-3 mb-3">
        <Button 
          variant={sendMode === 'immediate' ? 'primary' : 'outline-secondary'}
          style={{ 
            flex: 1, 
            borderRadius: '8px', 
            padding: '16px',
            textAlign: 'left'
          }}
          onClick={() => setSendMode('immediate')}
        >
          <div>
            <strong>⚡ {language === 'es' ? 'Inmediato' : 'Immediate'}</strong>
            <p style={{ fontSize: '0.85rem', marginBottom: 0, marginTop: '4px' }}>
              {language === 'es' ? 'Enviar ahora en cola' : 'Send now in queue'}
            </p>
          </div>
        </Button>
        <Button 
          variant={sendMode === 'scheduled' ? 'primary' : 'outline-secondary'}
          disabled
          style={{ 
            flex: 1, 
            borderRadius: '8px', 
            padding: '16px',
            textAlign: 'left'
          }}
          onClick={() => setSendMode('scheduled')}
        >
          <div>
            <strong>🕐 {language === 'es' ? 'Programado' : 'Scheduled'}</strong>
            <p style={{ fontSize: '0.85rem', marginBottom: 0, marginTop: '4px' }}>
              {language === 'es' ? 'Seleccionar fecha/hora' : 'Select date/time'}
            </p>
          </div>
        </Button>
      </div>
      
      {sendMode === 'scheduled' && (
        <div style={{ 
          backgroundColor: 'var(--bs-body-bg)',
          border: '1px solid var(--bs-border-color)',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  {language === 'es' ? 'Fecha' : 'Date'}
                </Form.Label>
                <Form.Control 
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  {language === 'es' ? 'Hora' : 'Time'}
                </Form.Label>
                <Form.Control 
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: SEND BUTTON
  // ───────────────────────────────────────────────────────────────────────
  
  const renderSendButton = () => {
    // ⭐ CAMBIO: Bloquear SIEMPRE si hay job activo en modo inmediato
    // Solo permitir múltiples si son programados
    const hasActiveJob = progress.isActive && (progress.status === 'processing' || progress.status === 'queued');
    const canSend = allValid && (sendMode === 'scheduled' || !hasActiveJob);
    
    return (
      <div className="d-grid">
        <Button 
          variant="success" 
          size="lg"
          disabled={!canSend || isSending}
          style={{ 
            borderRadius: '8px',
            padding: '16px',
            fontWeight: 600,
            opacity: (!canSend || isSending) ? 0.6 : 1
          }}
          onClick={handleSendStart}
        >
          {isSending ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {language === 'es' ? 'Guardando...' : 'Saving...'}
            </>
          ) : hasActiveJob && sendMode === 'immediate' ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
              {language === 'es' ? 'Procesando...' : 'Processing...'}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              {language === 'es' ? 'Iniciar Envío Masivo' : 'Start Bulk Send'}
            </>
          )}
        </Button>
        {!allValid && !isSending && (
          <small className="text-center mt-2" style={{ color: 'var(--bs-secondary)' }}>
            {language === 'es' 
              ? 'Completa todos los requisitos para continuar' 
              : 'Complete all requirements to continue'}
          </small>
        )}
        {hasActiveJob && sendMode === 'immediate' && (
          <Alert variant="warning" className="mt-3 mb-0">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            <small>
              {language === 'es' 
                ? 'Hay un envío en proceso. Espera a que termine o cambia a modo programado para encolar otro.' 
                : 'There is a send in process. Wait for it to finish or switch to scheduled mode to queue another.'}
            </small>
          </Alert>
        )}
        {progress.isActive && progress.status === 'queued' && (
          <Alert variant="info" className="mt-3 mb-0">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            <small>
              {language === 'es' 
                ? 'Sentinela procesará los contactos según los horarios configurados en Settings.' 
                : 'Sentinela will process contacts according to the schedule configured in Settings.'}
            </small>
          </Alert>
        )}
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: PREVIEW
  // ───────────────────────────────────────────────────────────────────────
  
  const renderPreview = () => {
    const sampleContacts = contacts.slice(0, 3);
    const hasMessage = message.trim().length > 0;
    
    return (
      <Card style={{ 
        border: 'none', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)',
        marginBottom: '24px'
      }}>
        <Card.Header style={{ 
          backgroundColor: 'var(--bs-body-bg)', 
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px' 
        }}>
          <h6 className="mb-0" style={{ fontWeight: 600 }}>
            <FontAwesomeIcon icon={faEye} className="me-2" />
            {language === 'es' ? 'Vista Previa' : 'Preview'}
          </h6>
        </Card.Header>
        <Card.Body style={{ padding: '20px' }}>
          {hasMessage && sampleContacts.length > 0 ? (
            <>
              <p style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '12px' }}>
                {language === 'es' 
                  ? `Muestra de ${Math.min(3, contacts.length)} de ${contacts.length} destinatarios:`
                  : `Sample of ${Math.min(3, contacts.length)} of ${contacts.length} recipients:`
                }
              </p>
              {sampleContacts.map((contact, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#25D366',
                    borderRadius: '12px 12px 12px 2px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    color: 'white',
                    fontSize: '0.9rem',
                    position: 'relative',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ 
                    fontSize: '0.75rem', 
                    opacity: 0.8, 
                    marginBottom: '4px',
                    fontWeight: 600 
                  }}>
                    {contact.nombre || contact.name || `Contacto ${idx + 1}`}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {message.replace(/\[nombre\]/gi, contact.nombre || contact.name || 'Usuario')}
                  </div>
                  <div style={{ 
                    fontSize: '0.7rem', 
                    opacity: 0.7, 
                    marginTop: '4px',
                    textAlign: 'right'
                  }}>
                    {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              {contacts.length > 3 && (
                <p style={{ 
                  fontSize: '0.8rem', 
                  color: 'var(--bs-secondary)', 
                  textAlign: 'center',
                  marginTop: '12px',
                  marginBottom: 0
                }}>
                  ... {language === 'es' ? 'y' : 'and'} {contacts.length - 3} {language === 'es' ? 'más' : 'more'}
                </p>
              )}
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: 'var(--bs-secondary)'
            }}>
              <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.3 }} />
              <p style={{ marginBottom: 0 }}>
                {language === 'es' 
                  ? 'Completa el mensaje y selecciona contactos para ver la vista previa'
                  : 'Complete the message and select contacts to see the preview'}
              </p>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // ⭐ NUEVO: RENDER PROGRESS CARD
  // ───────────────────────────────────────────────────────────────────────
  
  const renderProgressCard = () => (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)', 
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px 20px' 
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ fontWeight: 600 }}>
            {language === 'es' ? 'Estado del Envío' : 'Send Status'}
          </h6>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isPolling}
            style={{ borderRadius: '6px' }}
          >
            <FontAwesomeIcon icon={faSync} spin={isPolling} />
          </Button>
        </div>
      </Card.Header>
      <Card.Body style={{ padding: '20px' }}>
        {sendError && (
          <Alert variant="danger" className="mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <small>{sendError}</small>
          </Alert>
        )}
        
        {progress.isActive ? (
          <>
            <div className="text-center mb-3">
              <div className="mb-2">
                {progress.status === 'pending' && (
                  <Spinner animation="border" size="sm" variant="primary" />
                )}
                {progress.status === 'queued' && (
                  <FontAwesomeIcon icon={faClock} style={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                )}
                {progress.status === 'processing' && (
                  <Spinner animation="border" size="sm" variant="success" />
                )}
                {progress.status === 'completed' && (
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.5rem', color: '#10b981' }} />
                )}
                {progress.status === 'error' && (
                  <FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: '1.5rem', color: '#ef4444' }} />
                )}
              </div>
              
              <p style={{ color: 'var(--bs-body-color)', marginBottom: '8px', fontWeight: 600 }}>
                {progress.status === 'pending' && (language === 'es' ? 'Preparando...' : 'Preparing...')}
                {progress.status === 'queued' && (language === 'es' ? 'En Cola' : 'Queued')}
                {progress.status === 'processing' && (language === 'es' ? 'Enviando' : 'Sending')}
                {progress.status === 'completed' && (language === 'es' ? 'Completado' : 'Completed')}
                {progress.status === 'error' && (language === 'es' ? 'Error' : 'Error')}
              </p>
              
              <small style={{ color: 'var(--bs-secondary)', display: 'block' }}>
                {progress.message || (language === 'es' ? 'Procesando...' : 'Processing...')}
              </small>
            </div>
            
            <ProgressBar 
              now={progress.percentage} 
              style={{ height: '8px', borderRadius: '4px' }}
              variant={
                progress.status === 'error' ? 'danger' :
                progress.status === 'completed' ? 'success' :
                progress.status === 'processing' ? 'success' :
                progress.status === 'queued' ? 'warning' : 'primary'
              }
              animated={progress.status === 'pending' || progress.status === 'processing'}
            />
            
            <div className="d-flex justify-content-between mt-2">
              <small style={{ color: 'var(--bs-secondary)' }}>
                {progress.current} / {progress.total} {language === 'es' ? 'contactos' : 'contacts'}
              </small>
              <small style={{ color: 'var(--bs-secondary)' }}>
                {progress.percentage.toFixed(1)}%
              </small>
            </div>
            
            {activeJob && (
              <div className="mt-3 p-2 bg-light rounded">
                <small className="text-muted d-block">
                  <strong>{language === 'es' ? 'Job ID:' : 'Job ID:'}</strong> {activeJob.jobId}
                </small>
                <small className="text-muted d-block">
                  <strong>{language === 'es' ? 'Archivo:' : 'File:'}</strong> {activeJob.filePath.split('/').pop()}
                </small>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-center mb-3">
              <p style={{ color: 'var(--bs-secondary)', marginBottom: '8px' }}>
                {language === 'es' ? 'Sin envíos activos' : 'No active sends'}
              </p>
              <small style={{ color: 'var(--bs-secondary)' }}>
                {language === 'es' ? 'Listo para procesar' : 'Ready to process'}
              </small>
            </div>
            <ProgressBar 
              now={0} 
              style={{ height: '8px', borderRadius: '4px' }}
            />
            <div className="d-flex justify-content-between mt-2">
              <small style={{ color: 'var(--bs-secondary)' }}>
                0 / 0 {language === 'es' ? 'contactos' : 'contacts'}
              </small>
              <small style={{ color: 'var(--bs-secondary)' }}>0%</small>
            </div>
          </>
        )}
        
        {lastPollTime && (
          <div className="mt-3 text-center">
            <small style={{ color: 'var(--bs-secondary)', fontSize: '0.75rem' }}>
              {language === 'es' ? 'Última actualización:' : 'Last update:'} {lastPollTime.toLocaleTimeString()}
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // ⭐ NUEVO: RENDER JOBS LIST
  // ───────────────────────────────────────────────────────────────────────
  
  const renderJobsList = () => {
    const recentJobs = jobs.slice(0, 10);
    
    if (recentJobs.length === 0) {
      return null;
    }
    
    return (
      <Card style={{ 
        border: 'none', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)'
      }}>
        <Card.Header style={{ 
          backgroundColor: 'var(--bs-body-bg)', 
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px' 
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontWeight: 600 }}>
              📋 {language === 'es' ? 'Jobs Recientes' : 'Recent Jobs'}
            </h6>
            <Badge bg="primary">
              {jobs.filter(j => j.status === 'processing' || j.status === 'queued').length} {language === 'es' ? 'activos' : 'active'}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          <Table hover responsive style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: 'var(--bs-body-bg)' }}>
              <tr>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                  {language === 'es' ? 'Archivo' : 'File'}
                </th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                  {language === 'es' ? 'Contactos' : 'Contacts'}
                </th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                  {language === 'es' ? 'Progreso' : 'Progress'}
                </th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                  {language === 'es' ? 'Estado' : 'Status'}
                </th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                  {language === 'es' ? 'Fecha' : 'Date'}
                </th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.map((job) => (
                <tr key={job.jobId}>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                    <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {job.filePath.split('/').pop()}
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                    {job.sentContacts} / {job.totalContacts}
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                    <div style={{ width: '100px' }}>
                      <ProgressBar 
                        now={job.progress} 
                        style={{ height: '6px' }}
                        variant={
                          job.status === 'error' ? 'danger' :
                          job.status === 'completed' ? 'success' :
                          job.status === 'processing' ? 'success' : 'warning'
                        }
                      />
                      <small style={{ color: 'var(--bs-secondary)', fontSize: '0.75rem' }}>
                        {job.progress.toFixed(0)}%
                      </small>
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    <Badge 
                      bg={
                        job.status === 'completed' ? 'success' :
                        job.status === 'processing' ? 'primary' :
                        job.status === 'queued' ? 'warning' :
                        job.status === 'error' ? 'danger' : 'secondary'
                      }
                      style={{ fontSize: '0.75rem' }}
                    >
                      {job.status === 'completed' && (language === 'es' ? 'Completado' : 'Completed')}
                      {job.status === 'processing' && (language === 'es' ? 'Procesando' : 'Processing')}
                      {job.status === 'queued' && (language === 'es' ? 'En Cola' : 'Queued')}
                      {job.status === 'error' && (language === 'es' ? 'Error' : 'Error')}
                      {job.status === 'pending' && (language === 'es' ? 'Pendiente' : 'Pending')}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                    {new Date(job.createdAt).toLocaleString('es-CL', { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <Row className="g-4">
        {/* Panel Izquierdo - Control (70%) */}
        <Col lg={8}>
          {renderChecklist()}
          {renderEstimations()}
          
          <Card style={{ 
            border: 'none', 
            borderRadius: '12px', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            backgroundColor: 'var(--bs-body-bg)',
            marginBottom: '24px'
          }}>
            <Card.Header style={{ 
              backgroundColor: 'var(--bs-body-bg)', 
              borderBottom: '1px solid var(--bs-border-color)',
              padding: '16px 20px' 
            }}>
              <h5 className="mb-0" style={{ fontWeight: 600 }}>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                {language === 'es' ? 'Control de Envío' : 'Send Control'}
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '24px' }}>
              {renderSendModes()}
              {renderSendButton()}
            </Card.Body>
          </Card>
          
          {/* ⭐ NUEVO: Lista de Jobs */}
          {renderJobsList()}
        </Col>
        
        {/* Panel Derecho - Preview + Progress (30%) */}
        <Col lg={4}>
          {renderPreview()}
          {renderProgressCard()}
        </Col>
      </Row>
    </div>
  );
};

export default SendTab;